<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  parseDetailReportFilters,
  generateReportURLParams,
  generateSummaryEntityFilterParams,
} from './helpers/reportFilterHelper';
import ReportFiltersSection from './components/ReportFiltersSection.vue';
import SummaryDetailHeader from './components/SummaryDetailHeader.vue';
import SummaryDetailMetrics from './components/SummaryDetailMetrics.vue';
import SummaryEntityDetailCharts from './components/SummaryEntityDetailCharts.vue';
import SummaryDetailHeatmaps from './components/SummaryDetailHeatmaps.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { checkPermissions } = usePolicy();
const agentId = computed(() => route.params.id);
const agent = useFunctionGetter('agents/getAgentById', agentId);
const canViewAllReports = computed(() =>
  checkPermissions(['administrator', 'report_manage'])
);

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedInboxIds = ref([]);
const selectedTeamIds = ref([]);
const selectedLabelIds = ref([]);
const excludeLabelIds = ref([]);
const episodeResult = ref('all');

const applyEntityFilters = filters => {
  selectedInboxIds.value = filters.inboxIds || [];
  selectedTeamIds.value = filters.teamIds || [];
  selectedLabelIds.value = filters.labelIds || [];
  excludeLabelIds.value = filters.excludeLabelIds || [];
  episodeResult.value = filters.episodeResult || 'all';
};

const initializeFromURL = () => {
  const parsed = parseDetailReportFilters(route.query);
  from.value = parsed.from || 0;
  to.value = parsed.to || 0;
  businessHours.value = parsed.businessHours;
  applyEntityFilters(parsed);
};

initializeFromURL();

const detailFilters = computed(() => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  inboxIds: selectedInboxIds.value,
  teamIds: selectedTeamIds.value,
  labelIds: selectedLabelIds.value,
  excludeLabelIds: excludeLabelIds.value,
  agentIds: [Number(route.params.id)],
  episodeResult: episodeResult.value,
}));

const updateURLParams = () => {
  const entityParams = generateSummaryEntityFilterParams({
    inboxIds: selectedInboxIds.value,
    teamIds: selectedTeamIds.value,
    labelIds: selectedLabelIds.value,
    excludeLabelIds: excludeLabelIds.value,
    episodeResult: episodeResult.value,
  });
  const dateParams = generateReportURLParams({
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
  });
  router.replace({ query: { ...dateParams, ...entityParams } });
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  businessHours.value = date.businessHours;
  applyEntityFilters(entity);
  updateURLParams();
};

const openAgentActivity = () => {
  router.push({
    name: 'agent_activity_detail',
    params: { id: agent.value.id },
    query: {
      from: from.value,
      to: to.value,
    },
  });
};

onMounted(() => store.dispatch('agents/get'));
watch(() => route.query, initializeFromURL);
</script>

<template>
  <div v-if="agent.id" class="flex flex-col gap-4">
    <SummaryDetailHeader
      :title="agent.name"
      report-type="agent"
      :filters="detailFilters"
      :download-label="$t('AGENT_REPORTS.DOWNLOAD_AGENT_REPORTS')"
      :show-back-button="canViewAllReports"
      :show-download="canViewAllReports"
    >
      <V4Button
        v-if="canViewAllReports"
        :label="$t('AGENT_REPORTS.VIEW_ACTIVITY')"
        icon="i-lucide-timeline"
        size="sm"
        variant="outline"
        @click="openAgentActivity"
      />
    </SummaryDetailHeader>
    <ReportFiltersSection
      type="agent"
      hide-agent-filter
      @filters-apply="onFiltersApply"
    />
    <SummaryDetailMetrics
      type="agent"
      :entity-id="agent.id"
      :filters="detailFilters"
    />
    <SummaryEntityDetailCharts
      type="agent"
      :entity-id="agent.id"
      :filters="detailFilters"
    />
    <SummaryDetailHeatmaps
      entity-type="agent"
      :entity-id="agent.id"
      :filters="detailFilters"
    />
  </div>
  <div v-else class="w-full py-20">
    <Spinner class="mx-auto" />
  </div>
</template>
